import React, { useEffect, useState, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import '../styles/AgmDocumentsDash.css';
import '../styles/PublicationsDash.css';
import { FaPlusCircle, FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaTrash } from 'react-icons/fa';
import { RiCheckFill, RiCloseFill, RiFileShield2Line, RiToggleFill, RiToggleLine } from 'react-icons/ri';
import { useSlideout } from '../../../hooks/SlideoutContext';
import { IoMdRefresh } from 'react-icons/io';
import axios from 'axios';
import config from '../../../constants/config';
import axiosPost from '../../../helper/axios-helper';

export default function AgmDocumentsDash() {
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const { openSlideout, closeSlideout } = useSlideout();

  const fetchDocuments = () => {
    setLoading(true);
    axios.get(config.api.url + "admin/agm_documents/fetch", {
      headers: {
        api: config.api.key,
        auth: "b8GjLbLHDX9isF10ptWj"
      }
    })
    .then((json) => {
      const { success, data } = json.data;
      if (success) {
        setDocuments(data || []);
      } else {
        console.log("Error fetching documents");
      }
      setLoading(false);
    })
    .catch((error) => {
      console.error("Fetch error:", error);
      setLoading(false);
    });
  };

  const fetchEvents = () => {
    axios.get(config.api.url + "admin/agm_documents/events", {
      headers: {
        api: config.api.key,
        auth: "b8GjLbLHDX9isF10ptWj"
      }
    })
    .then((json) => {
      const { success, data } = json.data;
      if (success) {
        setEvents(data || []);
      }
    })
    .catch((error) => {
      console.error("Fetch events error:", error);
    });
  };

  const toggleDocumentStatus = (id, currentStatus) => {
    const newStatus = currentStatus > 0 ? 0 : 1;
    
    const formData = new FormData();
    formData.append("id", id);
    formData.append("status", newStatus);
    
    axios.post(config.api.url + "admin/agm_documents/toggle", formData, {
      headers: {
        api: config.api.key,
        auth: "b8GjLbLHDX9isF10ptWj",
        "Content-Type": "multipart/form-data"
      }
    })
    .then((json) => {
      const { success } = json.data;
      if (success) {
        fetchDocuments();
      }
    })
    .catch((error) => {
      console.error("Toggle status error:", error);
    });
  };

  const deleteDocument = (id) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }
    
    const formData = new FormData();
    formData.append("id", id);
    
    axios.post(config.api.url + "admin/agm_documents/delete", formData, {
      headers: {
        api: config.api.key,
        auth: "b8GjLbLHDX9isF10ptWj",
        "Content-Type": "multipart/form-data"
      }
    })
    .then((json) => {
      const { success } = json.data;
      if (success) {
        fetchDocuments();
      }
    })
    .catch((error) => {
      console.error("Delete error:", error);
    });
  };

  useEffect(() => {
    fetchDocuments();
    fetchEvents();
  }, []);

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFileAlt />;
    
    if (fileType.includes('pdf')) {
      return <FaFilePdf className="file-icon pdf" />;
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      return <FaFileWord className="file-icon word" />;
    } else if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) {
      return <FaFileExcel className="file-icon excel" />;
    } else if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) {
      return <FaFilePowerpoint className="file-icon powerpoint" />;
    }
    return <FaFileAlt className="file-icon" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const EditDocumentItem = ({ document, fetchDocuments, events }) => {
    const { closeSlideout } = useSlideout();
    const [itemData, setItemData] = useState({
      id: document.id || null,
      event_id: document.event_id || '',
      title: document.title || '',
      description: document.description || '',
      category: document.category || 'general',
      sort_order: document.sort_order || 0,
      active: document.active !== undefined ? document.active : 1
    });

    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(document.file_name || '');
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setItemData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? (checked ? 1 : 0) : value
      }));
    };

    const [compressing, setCompressing] = useState(false);

    const compressPDF = async (file) => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
        
        // Save with compression options
        const compressedBytes = await pdfDoc.save({
          useObjectStreams: true,
          addDefaultPage: false,
        });
        
        const compressedBlob = new Blob([compressedBytes], { type: 'application/pdf' });
        const compressedFile = new File([compressedBlob], file.name, { type: 'application/pdf' });
        
        console.log(`Original: ${(file.size / 1024 / 1024).toFixed(2)}MB, Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
        
        return compressedFile;
      } catch (error) {
        console.error('PDF compression failed:', error);
        return file; // Return original if compression fails
      }
    };

    const handleFileChange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        // Check if it's a PDF and larger than 5MB
        if (file.type === 'application/pdf' && file.size > 5 * 1024 * 1024) {
          setCompressing(true);
          setSelectedFileName(`Compressing ${file.name}...`);
          
          const compressedFile = await compressPDF(file);
          setSelectedFile(compressedFile);
          setSelectedFileName(`${file.name} (${(compressedFile.size / 1024 / 1024).toFixed(1)}MB)`);
          setCompressing(false);
        } else {
          setSelectedFile(file);
          setSelectedFileName(file.name);
        }
      }
    };

    const handleSelectFile = () => {
      fileInputRef.current.click();
    };

    const handleSaveChanges = () => {
      if (!itemData.event_id) {
        alert("Please select an event.");
        return;
      }
      if (!itemData.title) {
        alert("Please enter a title.");
        return;
      }
      if (!itemData.id && !selectedFile) {
        alert("Please select a document file.");
        return;
      }

      const formData = new FormData();
      
      if (itemData.id) {
        formData.append("id", itemData.id);
      }
      
      formData.append("event_id", itemData.event_id);
      formData.append("title", itemData.title);
      formData.append("description", itemData.description);
      formData.append("category", itemData.category);
      formData.append("sort_order", itemData.sort_order);
      formData.append("active", itemData.active);
      
      if (selectedFile) {
        formData.append("document", selectedFile);
      }
      
      const endpoint = itemData.id 
        ? "admin/agm_documents/edit" 
        : "admin/agm_documents/add";
      
      axios.post(config.api.url + endpoint, formData, {
        headers: {
          api: config.api.key,
          auth: "b8GjLbLHDX9isF10ptWj",
          "Content-Type": "multipart/form-data"
        }
      })
      .then((json) => {
        const { success, response } = json.data;
        if (success) {
          closeSlideout();
          fetchDocuments();
        } else {
          alert("Error: " + response);
        }
      })
      .catch((error) => {
        console.error("Save error:", error);
        alert("An error occurred while saving.");
      });
    };

    const categories = [
      { value: 'general', label: 'General' },
      { value: 'minutes', label: 'Minutes' },
      { value: 'reports', label: 'Reports' },
      { value: 'resolutions', label: 'Resolutions' },
      { value: 'financial', label: 'Financial Documents' },
      { value: 'presentations', label: 'Presentations' },
      { value: 'other', label: 'Other' }
    ];

    return (
      <div className="AgmDocumentEditForm">
        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Event *</div>
          <select
            name="event_id"
            value={itemData.event_id}
            onChange={handleChange}
            className="AgmDocumentEditFormSelect"
          >
            <option value="">Select an event...</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name || event.title}
              </option>
            ))}
          </select>
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Title *</div>
          <input
            type="text"
            name="title"
            value={itemData.title}
            onChange={handleChange}
            className="AgmDocumentEditFormInput"
            placeholder="Enter document title"
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Description</div>
          <textarea
            name="description"
            value={itemData.description}
            onChange={handleChange}
            className="AgmDocumentEditFormTextarea"
            placeholder="Enter document description (optional)"
            rows={3}
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Category</div>
          <select
            name="category"
            value={itemData.category}
            onChange={handleChange}
            className="AgmDocumentEditFormSelect"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Sort Order</div>
          <input
            type="number"
            name="sort_order"
            value={itemData.sort_order}
            onChange={handleChange}
            className="AgmDocumentEditFormInput"
            placeholder="0"
            min="0"
          />
        </div>

        <div className="AgmDocumentEditFormRow">
          <div className="AgmDocumentEditFormLabel">Document File {!itemData.id && '*'}</div>
          <div className="AgmDocumentEditFormFilePreview">
            {selectedFileName && (
              <div className="AgmDocumentEditFormFileName">
                <FaFileAlt /> {selectedFileName}
              </div>
            )}
            <button
              onClick={handleSelectFile}
              className="AgmDocumentEditFormFileButton"
              disabled={compressing}
            >
              {compressing ? 'Compressing...' : (itemData.id ? 'Replace File' : 'Select File')}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              style={{ display: 'none' }}
            />
            <div className="AgmDocumentEditFormFileHint">
              Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT
            </div>
          </div>
        </div>

        <div className="AgmDocumentEditFormRow">
          <label className="AgmDocumentEditFormCheckbox">
            <input
              type="checkbox"
              name="active"
              checked={itemData.active === 1}
              onChange={handleChange}
            />
            <span>Active (visible on website)</span>
          </label>
        </div>

        <div className="AgmDocumentEditFormActions">
          <button
            onClick={closeSlideout}
            className="AgmDocumentEditFormButton Cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            className="AgmDocumentEditFormButton Save"
          >
            {itemData.id ? 'Save Changes' : 'Add Document'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="AgmDocumentsDashWrapper">
      <div className="AgmDocumentsDashHeader">
        <div className="AgmDocumentsDashHeaderTitle">
          <h2>AGM Documents</h2>
          <p>Manage Annual General Meeting documents</p>
        </div>
        <div className="AgmDocumentsDashHeaderActions">
          <button
            className="AgmDocumentsDashHeaderButton Refresh"
            onClick={fetchDocuments}
          >
            <IoMdRefresh />
            <span>Refresh</span>
          </button>
          <button
            className="AgmDocumentsDashHeaderButton Add"
            onClick={() => {
              openSlideout({
                title: 'Add New AGM Document',
                content: <EditDocumentItem 
                  document={{}}
                  fetchDocuments={fetchDocuments}
                  events={events}
                />
              });
            }}
          >
            <FaPlusCircle />
            <span>Add New</span>
          </button>
        </div>
      </div>

      <div className="AgmDocumentsDashContent">
        {loading ? (
          <div className="AgmDocumentsDashLoading">Loading documents...</div>
        ) : (
          <>
            <div className="AgmDocumentsContentNavigation">
              <div className="AgmDocumentsContentNavigationItem First">Status</div>
              <div className="AgmDocumentsContentNavigationItem Type">Type</div>
              <div className="AgmDocumentsContentNavigationItem Title">Title</div>
              <div className="AgmDocumentsContentNavigationItem Event">Event</div>
              <div className="AgmDocumentsContentNavigationItem Category">Category</div>
              <div className="AgmDocumentsContentNavigationItem Size">Size</div>
              <div className="AgmDocumentsContentNavigationItem Date">Date</div>
              <div className="AgmDocumentsContentNavigationItem Actions">Actions</div>
            </div>
            <div className="AgmDocumentsScroll">
              {documents.length === 0 ? (
                <div className="AgmDocumentsEmpty">
                  <FaFileAlt className="EmptyIcon" />
                  <p>No documents found. Click "Add New" to upload your first AGM document.</p>
                </div>
              ) : (
                documents.map((item) => (
                  <div className="AgmDocumentItem" key={item.id}>
                    <div className="AgmDocumentItemDetailsText First Active">
                      <span className={`AgmDocumentItemDetailsTextOption Circle ${item.active > 0 ? 'Success' : 'Error'}`}>
                        {item.active > 0
                          ? <RiCheckFill style={{ fontSize: 12 }} />
                          : <RiCloseFill style={{ fontSize: 12 }} />
                        }
                      </span>
                    </div>
                    <div className="AgmDocumentItemDetailsText Type">
                      {getFileIcon(item.file_type)}
                    </div>
                    <div className="AgmDocumentItemDetailsText Title Bold">{item.title}</div>
                    <div className="AgmDocumentItemDetailsText Event">{item.event_name || '-'}</div>
                    <div className="AgmDocumentItemDetailsText Category">{item.category}</div>
                    <div className="AgmDocumentItemDetailsText Size">{formatFileSize(item.file_size)}</div>
                    <div className="AgmDocumentItemDetailsText Date">{formatDate(item.timestamp)}</div>

                    <div className="AgmDocumentItemOptions">
                      <a 
                        onClick={() => toggleDocumentStatus(item.id, item.active)} 
                        className={`AgmDocumentItemOptionsButton ${item.active > 0 ? 'DeleteInverted' : 'AddInverted'}`}
                      >
                        {item.active > 0
                          ? <RiToggleFill style={{ fontSize: 14 }} />
                          : <RiToggleLine style={{ fontSize: 14 }} />
                        }
                        <span className="AgmDocumentItemOptionsButtonText">
                          {item.active > 0 ? 'Deactivate' : 'Activate'}
                        </span>
                      </a>

                      <a 
                        onClick={() => {
                          openSlideout({
                            title: 'Edit AGM Document',
                            content: <EditDocumentItem 
                              document={item} 
                              fetchDocuments={fetchDocuments}
                              events={events}
                            />
                          });
                        }} 
                        className="AgmDocumentItemOptionsButton Edit"
                      >
                        <RiFileShield2Line style={{ fontSize: 14 }} />
                        <span className="AgmDocumentItemOptionsButtonText">Edit</span>
                      </a>

                      <a 
                        onClick={() => deleteDocument(item.id)} 
                        className="AgmDocumentItemOptionsButton Delete"
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                        <span className="AgmDocumentItemOptionsButtonText">Delete</span>
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
